import type { AutoGroupColumnDef, CellRendererSelectorFunc, IGroupCellRendererParams } from 'ag-grid-community';

/** Default GroupCellRenderer params for BlotterTable grids with grouped/tree rows. */
export const DEFAULT_GROUP_CELL_RENDERER_PARAMS = {
  /** Only the expand/collapse chevron should toggle groups; row clicks/double-clicks should not. */
  suppressDoubleClickExpand: true,
} as const satisfies Partial<IGroupCellRendererParams>;

function mergeGroupCellRendererParams(params: object | null | undefined) {
  return {
    ...DEFAULT_GROUP_CELL_RENDERER_PARAMS,
    ...params,
  };
}

/**
 * Applies {@link DEFAULT_GROUP_CELL_RENDERER_PARAMS} to an autoGroupColumnDef.
 * Supports both object and function `cellRendererParams` (AG Grid allows either).
 * Returns undefined when no def is supplied; callers that need AG Grid-generated group
 * columns to get defaults should pass a stub `{}` when init grouping/tree is active.
 * Callers can override by setting `suppressDoubleClickExpand: false` in cellRendererParams.
 */
export function withDefaultGroupCellRendererParams<TData>(
  autoGroupColumnDef: AutoGroupColumnDef<TData> | undefined
): AutoGroupColumnDef<TData> | undefined {
  if (autoGroupColumnDef == null) {
    return undefined;
  }

  const originalParams = autoGroupColumnDef.cellRendererParams;
  const originalSelector = autoGroupColumnDef.cellRendererSelector;

  const cellRendererParams =
    typeof originalParams === 'function'
      ? (params: unknown) => mergeGroupCellRendererParams(originalParams(params))
      : mergeGroupCellRendererParams(originalParams);

  const wrappedSelector: CellRendererSelectorFunc<TData> | undefined =
    originalSelector == null
      ? undefined
      : params => {
          const result = originalSelector(params);
          if (result == null) {
            return result;
          }

          return {
            ...result,
            params: mergeGroupCellRendererParams(result.params),
          };
        };

  return {
    ...autoGroupColumnDef,
    cellRendererParams,
    ...(wrappedSelector != null ? { cellRendererSelector: wrappedSelector } : {}),
  };
}
